// CommunityHub Component - Enhanced Community Entry Point

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Search,
  Users,
  UsersRound,
  Calendar,
  TrendingUp,
  Star,
  Heart,
  MapPin,
  MessageCircle,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ActivityFeed } from './ActivityFeed';
import { getPopularGroups } from '../../services/groupsService';
import { subscribeToPublicActivities } from '../../services/activityService';
import type { Group } from '../../types/groups';
import type { Activity } from '../../types/activity';

interface CommunityHubProps {
  onBack: () => void;
  onViewProfile: (userId: string) => void;
  onViewPlace: (placeId: string) => void;
  onViewGroup: (groupId: string) => void;
  onExploreGroups: () => void;
  onSearchUsers: () => void;
  onViewMessages: () => void;
  onViewEvents: () => void;
}

type Tab = 'activity' | 'discover';

export function CommunityHub({
  onBack,
  onViewProfile,
  onViewPlace,
  onViewGroup,
  onExploreGroups,
  onSearchUsers,
  onViewMessages,
  onViewEvents,
}: CommunityHubProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('activity');
  const [activities, setActivities] = useState<Activity[]>([]);
  const [popularGroups, setPopularGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  // Load activities and recommendations
  useEffect(() => {
    setLoading(true);

    // Subscribe to real-time activities
    const unsubActivities = subscribeToPublicActivities((acts) => {
      setActivities(acts);
      setLoading(false);
    }, 20);

    // Load popular groups
    getPopularGroups(4).then(setPopularGroups).catch(console.error);

    return () => unsubActivities();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Community</h1>
            <div className="flex-1" />
            <button
              onClick={onSearchUsers}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Search className="w-5 h-5" />
            </button>
            {user && (
              <button
                onClick={onViewMessages}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            <button
              onClick={onViewEvents}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-rose-50 text-rose-700 rounded-full text-sm font-medium hover:bg-rose-100 transition-colors"
            >
              <Calendar className="w-4 h-4" />
              Events
            </button>
            <button
              onClick={onSearchUsers}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium hover:bg-indigo-100 transition-colors"
            >
              <Users className="w-4 h-4" />
              Mitglieder finden
            </button>
            <button
              onClick={onExploreGroups}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-100 transition-colors"
            >
              <UsersRound className="w-4 h-4" />
              Gruppen entdecken
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'activity'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Aktivitaeten
            </button>
            <button
              onClick={() => setActiveTab('discover')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'discover'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Star className="w-4 h-4 inline mr-2" />
              Entdecken
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {activeTab === 'activity' && (
          <ActivityFeed
            activities={activities}
            loading={loading}
            onViewProfile={onViewProfile}
            onViewPlace={onViewPlace}
            onViewGroup={onViewGroup}
          />
        )}

        {activeTab === 'discover' && (
          <div className="space-y-6">
            {/* Popular Groups */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Beliebte Gruppen</h2>
                <button
                  onClick={onExploreGroups}
                  className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                >
                  Alle anzeigen
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {popularGroups.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {popularGroups.map((group) => (
                    <button
                      key={group.id}
                      onClick={() => onViewGroup(group.id)}
                      className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-gray-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center gap-3">
                        {group.avatarUrl || group.coverImageUrl ? (
                          <img
                            src={group.avatarUrl || group.coverImageUrl}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <UsersRound className="w-6 h-6 text-indigo-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">
                            {group.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {group.memberCount} Mitglieder
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                  <UsersRound className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">Noch keine Gruppen vorhanden</p>
                  <button
                    onClick={onExploreGroups}
                    className="mt-3 text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    Gruppe erstellen
                  </button>
                </div>
              )}
            </section>

            {/* Community Stats */}
            <section className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
              <h2 className="text-lg font-semibold mb-4">Community Highlights</h2>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold">{activities.length}+</div>
                  <div className="text-sm text-white/80">Aktivitaeten</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{popularGroups.length}</div>
                  <div className="text-sm text-white/80">Gruppen</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {new Set(activities.map(a => a.userId)).size}
                  </div>
                  <div className="text-sm text-white/80">Aktive Nutzer</div>
                </div>
              </div>
            </section>

            {/* Join CTA for logged out users */}
            {!user && (
              <section className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                <Users className="w-12 h-12 mx-auto text-indigo-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Werde Teil der Community!
                </h3>
                <p className="text-gray-600 mb-4">
                  Melde dich an, um Mitglieder zu finden, Gruppen beizutreten und deine Lieblingsorte zu teilen.
                </p>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default CommunityHub;
